/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// 通用的全屏弹窗mixin组件，支持两种异步结果：resolve & reject
import router from '@/router';
import { Popup } from 'vant';
import { VueConstructor } from 'vue';
import { Vue, Component, Watch } from 'vue-property-decorator';
import store from '@/store';
import type { PopupViewComponent } from './types';

interface InnerComponent extends VueConstructor {
  showInPopup?(): void,
  hideInPopup?(): void,
}

@Component
export default class PopupView extends Vue {
  public visible = false;

  // 外部组件以及相关的props属性
  private InnerComponent: InnerComponent | null = null;

  private payload: Dictionary = Object.create({});

  // 需要在具体的子组件中手动触发resolve;
  protected resolve!: PromiseResolve<void | any>;

  protected reject!: PromiseReject;

  // 单例对象
  public static instance: PopupView | null;

  // 是否需要更新组件数据
  private needRefreshInnerComponent = false;

  // 路由发生跳转的时候关闭弹窗页面
  @Watch('$route')
  private onRouteChange() {
    PopupView.close();
  }

  // 获取单例
  private static getInstance(): PopupView {
    if (!PopupView.instance) {
      PopupView.instance = new PopupView({
        router,
        store,
      }).$mount();

      document.body.appendChild(PopupView.instance.$el);
    }

    return PopupView.instance;
  }

  // API调用显示弹窗页面的方法，并在resolve或者reject之后隐藏弹窗；
  // show方法具体的参数类型依据子类update方法的参数定义为准；
  public static show<T = void>(
    InnerComponent: InnerComponent,
    payload?: Dictionary,
    /** 是否需要更新子组件 */
    needRefreshInnerComponent?: boolean,
  ): Promise<T> {
    const instance = PopupView.getInstance();
    instance.InnerComponent = InnerComponent;
    instance.payload = payload || Object.create({});
    // 需要更新的场景，更新子组件数据
    if (needRefreshInnerComponent) {
      instance.needRefreshInnerComponent = needRefreshInnerComponent;
    }

    return new Promise((resolve, reject) => {
      instance.resolve = resolve;
      instance.reject = reject;

      instance.$nextTick(() => {
        instance.preCheckPopup().then((show: boolean) => {
          instance.visible = show;

          if (!instance.visible) {
            instance.resolve(null);
          }

          const $innerRef = instance.$refs.innerRef as unknown as InnerComponent;

          if ($innerRef && typeof $innerRef.showInPopup === 'function') {
            $innerRef.showInPopup.call($innerRef);
          }
        });
      });
    });
  }

  // API调用关闭弹窗页面
  public static close() {
    const instance = PopupView.getInstance();

    if (instance && instance.visible) {
      instance.visible = false;
      // 防止弹窗关闭后，短时间内会重新挂载这个popup导致InnerComponent组件内部的逻辑被初始化调用
      instance.InnerComponent = null;
      // TODO 兼容关闭动画
      instance.reject();
    }
    // 关闭时重置数据
    // TODO: 销毁方案不可行，会导致数据缓存出问题；
    instance.needRefreshInnerComponent = false;

    const $innerRef = instance.$refs.innerRef as unknown as InnerComponent;

    if ($innerRef && typeof $innerRef.hideInPopup === 'function') {
      $innerRef.hideInPopup.call($innerRef);
    }
  }

  // @Override 自定义前置验证方法，根据返回的Promise<boolean>决定是展示弹窗还是直接resolve掉
  protected async preCheckPopup(): Promise<boolean> {
    const $innerRef = this.$refs.innerRef as Vue & PopupViewComponent;

    if ($innerRef && typeof $innerRef.preCheckPopup === 'function') {
      const show = await $innerRef.preCheckPopup();

      // 如果需要显示，则调用初始化接口
      if (show && typeof $innerRef.initData === 'function') {
        $innerRef.initData();
      }

      return show;
    }

    return Promise.resolve(true);
  }

  private render() {
    const { InnerComponent } = this;
    const innerProps = {
      ref: 'innerRef',
      attrs: {
        ...this.payload,
        visible: this.visible,
      },
      on: { },
      key: this.needRefreshInnerComponent ? new Date().getTime() : 1,
    };

    if (this.resolve && this.reject) {
      innerProps.on = {
        resolve: (val: unknown) => {
          this.resolve(val);
          PopupView.close();
        },
        reject: this.reject,
      };
    }

    return (
      <Popup
        v-model={this.visible}
        position="right"
        lazyRender={false}
        style={{ width: '100%', height: '100%' }}
      >
        { InnerComponent ? <InnerComponent {...innerProps} /> : null}
      </Popup>
    );
  }
}
